import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ItemToReturn } from '@src/models/returns';
import { useSummaryStore } from '@src/stores/summaryStore';
import { SkeletonLine } from '@one-thd/sui-atomic-components';
import {
  persistReturnSummaryinfo,
  buildInStoreReturnSummaryData,
} from '@src/utils/finalizeReturnUtil';
import useMultiCallCreateReturn from '@src/hooks/useMultiCallCreateReturn';
import { useProcessValidator } from '@src/hooks/useProcessValidator';
import { useItemEditRemove } from '@src/hooks/useItemEditRemove';
import { useRefundCalculator } from '@src/hooks/useRefundCalculator';
import { useShipmentSeparation } from '@src/hooks/useShipmentSeparation';
import { emitReturnsFlowStep } from '@src/utils/returns-instrumentation-context';
import {
  TagOnConfirmReturn,
  TagOnFinalizeReturnPage,
  TagScheduleRPMFinalizeReturn,
} from '@src/utils/analytics-tagging';
import { isScheduledPickupEnabled } from '@src/constants/fs-values';
import { useReturnStore } from '../../stores/returnStore';
import ReturnsLoadingFlow from './ReturnsLoadingFlow';
import {
  isClientSideOnlyMethod,
  RETURN_METHOD,
  ReturnMethodType,
} from '../../constants/general';
import { ReturnButtons } from '../shared/ReturnButtons';
import { ReturnHeaders } from '../shared/ReturnHeaders';
import { RefundTotal } from '../shared/RefundTotal';
import { goToPreviousStep } from '../../stores/newFlowStore';
import FinalizeReturnError from '../finalize-return/FinalizeReturnError';
import FraudModal from '../shared/fraud/FraudModal';
import ErrorBanner from '../shared/ErrorBanner';
import RemoveItemModal from './modal/RemoveItemModal';
import { ReturnCreateErrorStep } from './ReturnCreateErrorStep';
import { ReturnMethodCards } from './ReturnMethodCards';

const SUBMISSION_STEPS = {
  UNSUBMITTED: 'unsubmitted',
  SUBMITTING: 'submitting',
  ERROR: 'returnCreateError',
} as const;

type SubmissionStep = (typeof SUBMISSION_STEPS)[keyof typeof SUBMISSION_STEPS];

const finalizePageVariantByMethod: Record<ReturnMethodType, string> = {
  [RETURN_METHOD.IN_STORE]: 'in store',
  [RETURN_METHOD.SORIS]: 'soris',
  [RETURN_METHOD.UPS_DROP_OFF]: 'ups',
  [RETURN_METHOD.LORP]: 'return pickup',
  [RETURN_METHOD.APO_FPO]: 'apo fpo',
};

const getFinalizePageVariant = (selectedItems: ItemToReturn[]) => {
  const uniqueMethods = [
    ...new Set(
      selectedItems
        .map((item) => item.returnMethod)
        .filter(Boolean) as ReturnMethodType[],
    ),
  ];

  if (uniqueMethods.length !== 1) {
    return 'split returns';
  }

  return finalizePageVariantByMethod[uniqueMethods?.[0]] || 'split returns';
};

const ReviewReturnDetailsStep = () => {
  const selectedItems = useReturnStore((state) => state.selectedItems);
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const refundValues = useSummaryStore((state) => state.refundValues);
  const returnShipments = useReturnStore((state) => state.returnShipments);
  const orderDetailsPath = useReturnStore((state) => state.orderDetailsPath);
  const returnEligibilityDetails = useReturnStore(
    (state) => state.returnEligibilityDetails,
  );
  const isMilitaryExchangeGuest = useReturnStore(
    (state) => state.isMilitaryExchangeGuest,
  );
  const navigate = useNavigate();
  const [submissionStep, setSubmissionStep] = useState<SubmissionStep>(
    SUBMISSION_STEPS.UNSUBMITTED,
  );
  const [showLorpCheckboxError, setShowLorpCheckboxError] = useState(false);
  const hasTaggedFinalizePageRender = useRef(false);
  // Guards the client-side (In-Store) return_created emit against a double-click
  // landing before navigation unmounts this step — mirrors the API path's guard
  // (useMultiCallCreateReturn). Per-mount, so a legitimate re-finalize after
  // remount still emits.
  const hasEmittedClientSideCreated = useRef(false);
  const { isRefundCalculatorLoading, calculateRefund } = useRefundCalculator({
    orderDetailsV2,
  });

  // Callback used when policy validator is successful
  const recalculateRefund = (modifiedSelectedItems?: ItemToReturn[]) =>
    calculateRefund(modifiedSelectedItems || selectedItems);

  const {
    processPolicyValidator,
    renderModal,
    returnFraudType,
    policyValidatorAlert,
    setPolicyValidatorAlert,
    isPolicyValidatorLoading,
  } = useProcessValidator({
    orderDetailsV2,
    returnEligibilityDetails,
    isMilitaryExchangeGuest,
    proceedToNextStep: recalculateRefund,
  });

  const {
    onEditItemSubmit,
    onRemoveItemSubmit,
    onConfirmRemoveItem,
    onCancelRemoveItem,
    renderRemoveItemModal,
  } = useItemEditRemove({
    selectedItems,
    processPolicyValidator,
    setPolicyValidatorAlert,
    proceedToNextStep: recalculateRefund,
    orderDetailsPath,
    isMilitaryExchangeGuest,
  });

  const { lorpShipments } = useShipmentSeparation(returnShipments);

  const { APICallState, makeCreateReturnCalls, apiError, isLoading } =
    useMultiCallCreateReturn();

  const militaryExchange = useReturnStore((state) => state.militaryExchange);
  const tenders = useReturnStore((state) => state.tenders);
  const isPTCOrder = useReturnStore((state) => state.isPTCOrder);

  // Helper function to check if there are unagreed LORP shipments
  const hasUnagreedLorpShipments = () =>
    lorpShipments?.some((shipment) => !shipment.hasAgreed) ?? false;

  // Clear error when all LORP shipments are agreed
  useEffect(() => {
    if (!hasUnagreedLorpShipments() && showLorpCheckboxError) {
      setShowLorpCheckboxError(false);
    }
  }, [lorpShipments, showLorpCheckboxError]);

  useEffect(() => {
    if (
      submissionStep === SUBMISSION_STEPS.UNSUBMITTED &&
      !hasTaggedFinalizePageRender.current
    ) {
      TagOnFinalizeReturnPage(
        militaryExchange,
        getFinalizePageVariant(selectedItems),
      );
      // New Relic: funnel — the review/finalize screen was reached.
      emitReturnsFlowStep('review_viewed');
      hasTaggedFinalizePageRender.current = true;
    }
  }, [militaryExchange, selectedItems, submissionStep]);

  const onButtonContinueClick = () => {
    // Check if there are unagreed LORP shipments
    if (hasUnagreedLorpShipments()) {
      setShowLorpCheckboxError(true);
      return;
    }

    // Schedule RPM (5.2): LORP T&C agreed and continue clicked to finalize.
    if (
      isScheduledPickupEnabled() &&
      lorpShipments &&
      lorpShipments.length > 0
    ) {
      TagScheduleRPMFinalizeReturn();
    }

    TagOnConfirmReturn(
      selectedItems,
      militaryExchange,
      tenders?.[0]?.type,
      orderDetailsV2,
      refundValues,
      isPTCOrder,
    );
    const hasAllClientSideItems = selectedItems.every((item) =>
      isClientSideOnlyMethod(item.returnMethod),
    );

    // If all items are client-side only (in-store or APO/FPO), skip the return creation call and navigate directly to the return summary page
    if (hasAllClientSideItems && returnShipments) {
      // New Relic: conversion — client-side (In-Store) returns have no createReturn
      // API call, so emit return_created here at the finalize→summary hand-off to keep
      // the funnel's "Created" step honest. The API paths emit this in
      // useMultiCallCreateReturn / useKeepItCreateReturn; without this, client-side
      // returns reached "Summary" without ever counting as "Created". Guarded so a
      // double-click before navigation unmounts can't double-count.
      if (!hasEmittedClientSideCreated.current) {
        hasEmittedClientSideCreated.current = true;
        emitReturnsFlowStep('return_created', { result: 'success' });
      }
      const returnSummaryData = buildInStoreReturnSummaryData(
        orderDetailsV2?.orderNumber || '',
        orderDetailsV2?.emailId || '',
        selectedItems,
        refundValues,
        returnShipments,
      );
      persistReturnSummaryinfo(returnSummaryData, navigate);
      return;
    }

    makeCreateReturnCalls();

    // Only show skeleton loader for LORP returns
    const hasLorpItems = lorpShipments && lorpShipments.length > 0;
    if (hasLorpItems) {
      setSubmissionStep(SUBMISSION_STEPS.SUBMITTING);
    }
  };

  const onRetryFailedCalls = () => {
    // Show skeleton loader during retry
    setSubmissionStep(SUBMISSION_STEPS.SUBMITTING);

    // Retry the calls - this will continue from currentStep which is already positioned
    // after successful calls, and completedCalls already has successful ones preserved
    makeCreateReturnCalls();
  };

  useEffect(() => {
    if (
      !isLoading &&
      submissionStep === SUBMISSION_STEPS.SUBMITTING &&
      apiError !== null
    ) {
      // Handle failed submission
      setSubmissionStep(SUBMISSION_STEPS.ERROR);
    }
  }, [isLoading, submissionStep, apiError]);

  const headerTitle =
    submissionStep === SUBMISSION_STEPS.ERROR
      ? 'Review the items below'
      : 'Review your return details';

  const renderUnsubmittedState = () => (
    <>
      {apiError && <FinalizeReturnError error={apiError} />}
      {policyValidatorAlert && <ErrorBanner />}
      <ReturnMethodCards
        onEditItemSubmit={onEditItemSubmit}
        onRemoveItemSubmit={onRemoveItemSubmit}
        isPolicyValidatorLoading={isPolicyValidatorLoading}
        showLorpCheckboxError={showLorpCheckboxError}
      />
      {isRefundCalculatorLoading ? (
        <div className="sui-mt-4" data-testid="method-skeleton">
          <SkeletonLine variant="multi" numberOfLines={2} />
        </div>
      ) : (
        <div className="sui-mt-8">
          <RefundTotal />
        </div>
      )}
      <ReturnButtons
        proceedBack={goToPreviousStep}
        proceedNext={onButtonContinueClick}
        loading={isLoading}
        final
      />
      {renderModal && (
        <FraudModal
          returnFraudType={returnFraudType}
          title={renderModal.title}
          body={renderModal.bodyMessage}
          buttonText={renderModal.buttonMessage}
          onClick={renderModal.onClick}
        />
      )}
      {renderRemoveItemModal && (
        <RemoveItemModal
          onConfirmRemove={onConfirmRemoveItem}
          onCancelRemove={onCancelRemoveItem}
        />
      )}
    </>
  );
  return (
    <div className="sui-max-w-[555px]">
      <ReturnHeaders title={headerTitle} />
      {submissionStep === SUBMISSION_STEPS.UNSUBMITTED &&
        renderUnsubmittedState()}
      {submissionStep === SUBMISSION_STEPS.SUBMITTING && (
        <ReturnsLoadingFlow apiCallState={APICallState} />
      )}
      {submissionStep === SUBMISSION_STEPS.ERROR && (
        <ReturnCreateErrorStep
          APICallStateProp={APICallState}
          onRetry={onRetryFailedCalls}
          code={apiError?.code}
        />
      )}
    </div>
  );
};

export default ReviewReturnDetailsStep;
